import React from 'react';
import { useNavigate } from 'react-router-dom';
import { School, ArrowRight, Info } from 'lucide-react';

interface EnrollmentRequiredBannerProps {
  role?: 'school_staff' | 'student' | 'guardian' | string;
}

const roleMessages: Record<string, { title: string; body: string }> = {
  school_staff: {
    title: 'Welcome! You\'re not yet enrolled in a school.',
    body: 'To access your teaching dashboard, you need to be enrolled in a school by an administrator, or enroll yourself using a school code.',
  },
  student: {
    title: 'Welcome! You\'re not yet enrolled in a school.',
    body: 'To access your student dashboard, you need to be enrolled in a school. Please ask your school administrator to add you, or use your school\'s enrollment code.',
  },
  guardian: {
    title: 'Welcome! You\'re not yet linked to a school.',
    body: 'To track your child\'s progress and communicate with teachers, you need to be linked to a school. Please contact your school\'s administrator.',
  },
};

const EnrollmentRequiredBanner: React.FC<EnrollmentRequiredBannerProps> = ({ role }) => {
  const navigate = useNavigate();
  const message = roleMessages[role ?? ''] ?? {
    title: 'Welcome! You\'re not yet enrolled in a school.',
    body: 'Please go to Settings to enroll in or link to a school.',
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-blue-500/5 dark:bg-blue-500/10 blur-3xl" />
      </div>

      <div className="relative w-full max-w-lg">
        {/* Card */}
        <div className="bg-white dark:bg-gray-900 border border-blue-100 dark:border-blue-900/40 rounded-2xl shadow-lg shadow-blue-500/5 dark:shadow-blue-900/20 overflow-hidden">
          {/* Top accent bar */}
          <div className="h-1 w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

          <div className="p-8">
            {/* Icon */}
            <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800/50 mb-6 mx-auto">
              <School className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>

            {/* Text */}
            <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-3">
              {message.title}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center leading-relaxed mb-8">
              {message.body}
            </p>

            {/* Info callout */}
            <div className="flex items-start gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40 rounded-xl p-4 mb-6">
              <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                You can access <strong>Settings</strong> from the sidebar to manage your enrollment. All other features will be available once you are linked to a school.
              </p>
            </div>

            {/* CTA */}
            <button
              onClick={() => navigate('/dashboard/settings')}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25 active:scale-[0.98] group"
            >
              Go to Settings
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        {/* Subtle bottom note */}
        <p className="text-center text-xs text-gray-400 dark:text-gray-600 mt-4">
          Need help? Contact your school administrator.
        </p>
      </div>
    </div>
  );
};

export default EnrollmentRequiredBanner;
